import { useState, useEffect, type CSSProperties, type MouseEvent } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { HEADER_CTAS } from "~/config/navigation";
import { MegaMenu } from "./MegaMenu";
import { MobileMenu } from "./MobileMenu";
import { Hamburger } from "~/components/icons";

/**
 * Site header with mega-menu, mobile menu, and header modes.
 *
 * Modes (matching Hugo's header_mode param):
 *   default      — white background, dark text
 *   light        — brand white background, dark text
 *   dark         — dark background, light text
 *   dark-locked  — always dark, engineering cyan accents
 *   overlay      — transparent, becomes white on scroll past 64px
 *
 * Dark modes use Tailwind v4's class-based dark variant:
 *   @custom-variant dark (&:where(.dark, .dark *));
 * The `dark` class is added to <header> and all child elements
 * use `dark:` utilities — same pattern as Hugo's Tailwind v3.
 *
 * Matches: themes/conductorone/layouts/partials/header.html
 */
export type HeaderMode = "default" | "light" | "white" | "dark" | "dark-flat" | "dark-locked" | "overlay" | "scroll-dark";

/**
 * Derive header mode from the current URL path.
 * Matches Hugo's frontmatter header_mode values for retained pages.
 */
const HEADER_MODE_RULES: Array<{
  match: (path: string) => boolean;
  mode: HeaderMode;
}> = [
  {
    match: (p) =>
      p === "/resources/agentic-adaptation-playbook" ||
      p === "/resources/agentic-adaptation-playbook/",
    mode: "light",
  },
  { match: (p) => p === "/" || p === "", mode: "scroll-dark" },
  { match: (p) => p === "/platform-overview" || p === "/platform-overview/", mode: "dark" },
  // Legacy Identity Security Platform and Agentic Vault routes render dark
  // while their client-side redirects resolve to current destinations.
  { match: (p) => p === "/products/identity-security-platform" || p === "/products/identity-security-platform/", mode: "dark-flat" },
  { match: (p) => p === "/products/agentic-vault" || p === "/products/agentic-vault/", mode: "dark-flat" },
  { match: (p) => p.startsWith("/products/"), mode: "dark" },
  { match: (p) => p === "/pricing" || p.startsWith("/pricing/"), mode: "dark" },
  { match: (p) => p.startsWith("/integrations"), mode: "dark" },
  // The Security hero is white (like /about-us below), so the nav has to be too —
  // otherwise the dark bar sits directly on the white hero with a hard seam.
  { match: (p) => p === "/security" || p === "/security/", mode: "white" },
  { match: (p) => p.startsWith("/partners"), mode: "dark" },
  { match: (p) => p === "/press-kit" || p === "/press-kit/", mode: "dark" },
  { match: (p) => p === "/lp/request-demo" || p === "/lp/request-demo/", mode: "dark" },
  { match: (p) => p === "/customers" || p === "/customers/", mode: "dark" },
  { match: (p) => p === "/c1-transform-2026" || p === "/c1-transform-2026/", mode: "white" },
  // The About hero is white (mirroring the homepage hero), and the nav is a sibling
  // above <main> with nothing behind it but the page — so without this it stays cream
  // and leaves a seam across the top of the hero.
  { match: (p) => p === "/about-us" || p === "/about-us/", mode: "white" },
];

function useHeaderMode(propMode?: HeaderMode): HeaderMode {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (propMode && propMode !== "default") return propMode;
  for (const rule of HEADER_MODE_RULES) {
    if (rule.match(pathname)) return rule.mode;
  }
  return "default";
}

export function Header({ mode: propMode }: { mode?: HeaderMode }) {
  const mode = useHeaderMode(propMode);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [atTop, setAtTop] = useState(true);

  /**
   * Logo click handler — when already on the home page, prevent the
   * Link from triggering a same-route navigation (which can fail with
   * a deserialization error in production builds) and just scroll to
   * the top instead.
   */
  const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (pathname === "/" || pathname === "") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (mode === "overlay") {
      const handleScroll = () => setScrolled(window.scrollY > 64);
      handleScroll();
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => window.removeEventListener("scroll", handleScroll);
    }
    if (mode === "scroll-dark") {
      const handleScroll = () => {
        // Markers sit on the dark bands below the hero. Use the first one that's
        // actually rendered: the mobile logo band is display:none at desktop widths and
        // reports an all-zero rect, where the stats band is the first dark section
        // instead. Without the height filter a hidden marker reads as "already scrolled
        // past" and darkens the header on load.
        const rect = Array.from(document.querySelectorAll("[data-dark-section]"))
          .map((el) => el.getBoundingClientRect())
          .find((r) => r.height > 0);
        setScrolled(!!rect && rect.top <= 64);
        setAtTop(window.scrollY <= 0);
      };
      handleScroll();
      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [mode]);

  const scrollDarkActive = mode === "scroll-dark" && scrolled;
  const isDarkFlat = mode === "dark-flat";
  const isDark = mode === "dark" || mode === "dark-flat" || mode === "dark-locked" || scrollDarkActive;
  const isEngineering = mode === "dark-locked";
  const isOverlayTransparent = mode === "overlay" && !scrolled;
  const isLight = mode === "light";
  const isWhite = mode === "white";

  // Background: engineering gets eng-void, dark-flat gets #141414 for legacy
  // redirect surfaces, generic dark gets #231F20.
  const bgClass = isEngineering
    ? "bg-eng-void"
    : isDarkFlat
      ? "bg-[#141414]"
      : isDark
        ? "bg-[#231F20]"
        : isWhite
          ? "bg-white"
          : isLight
          ? "bg-[#F7F7F5]"
          : isOverlayTransparent
          ? "bg-transparent"
          : mode === "scroll-dark"
            ? // Resting at the very top, the nav continues the hero's pink wash so it
              // reads as one surface. The moment the page moves it becomes a solid bar,
              // and the dark band below takes over from there (see isDark above).
              atTop
              ? "bg-white home-hero-wash"
              : "bg-white"
            : "bg-cream";

  const headerStyle = isLight
    ? ({
        "--color-nav-text": "#231F20",
      } as CSSProperties)
    : undefined;

  // Logo switches to the light version in any dark context. Both variants are
  // rendered and toggled with `hidden`, rather than swapping one <img>'s src:
  // a src swap leaves the old (now same-colour-as-the-bar) logo on screen until
  // the new file downloads, which reads as the logo vanishing.
  const logoOnDark = isDark || isOverlayTransparent;
  const logoClass = "h-[22px] w-auto";

  return (
    <>
      <header
        id="header"
        className={`sticky top-0 z-50 px-4 transition-colors duration-50 has-[[data-state=open]]:[box-shadow:inset_0_-1px_0_0_var(--color-nav-border)] dark:has-[[data-state=open]]:[box-shadow:inset_0_-1px_0_0_rgba(255,255,255,0.1)] md:px-8 ${bgClass} ${isDark ? "dark" : ""}`}
        style={headerStyle}
      >
        <div className="relative mx-auto flex h-16 max-w-screen-xl items-center justify-between">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex shrink-0 items-center">
            <img
              src="/images/c1-ai-logo-black.webp"
              alt={logoOnDark ? "" : "C1.ai"}
              className={`${logoClass} ${logoOnDark ? "hidden" : ""}`}
            />
            <img
              src="/images/c1-ai-logo-white.webp"
              alt={logoOnDark ? "C1.ai" : ""}
              className={`${logoClass} ${logoOnDark ? "" : "hidden"}`}
            />
          </Link>

          {/* Desktop mega-menu */}
          <MegaMenu isEngineering={isEngineering} />

          {/* Desktop CTAs */}
          <div className="hidden items-center gap-3 lg:flex">
            {/* Sign In */}
            <a
              href={HEADER_CTAS.signIn.url}
              className={`inline-flex rounded-lg border border-transparent px-4 py-2.5 font-body text-sm font-semibold tracking-[-0.01em] transition-colors duration-300 ease-in-out ${
                isEngineering
                  ? "bg-transparent text-eng-electric hover:border-eng-electric-dark hover:bg-eng-electric-extraLight hover:text-eng-electric-light"
                  : "bg-transparent text-teal-dark hover:border-teal-dark hover:bg-teal-dark hover:text-white dark:bg-transparent dark:text-gray-20 dark:hover:border-white dark:hover:bg-white/10 dark:hover:text-white"
              }`}
            >
              {HEADER_CTAS.signIn.label}
            </a>
            {/* Book a Demo */}
            <a
              href={HEADER_CTAS.bookDemo.url}
              className={`inline-flex rounded-lg px-4 py-2.5 font-body text-sm font-semibold tracking-[-0.01em] transition-all duration-300 ease-in-out ${
                isEngineering
                  ? "bg-eng-electric text-eng-void hover:bg-eng-electric-light hover:shadow-[0_0_20px_rgba(0,255,204,0.4)]"
                  : "bg-teal-cta text-white hover:bg-teal-cta-hover active:bg-teal-cta-hover/90"
              }`}
            >
              {HEADER_CTAS.bookDemo.label}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="-mr-1.5 flex h-11 w-11 items-center justify-center text-gray-80 dark:text-white lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Hamburger />
          </button>
        </div>
      </header>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isDark={isDark}
        isEngineering={isEngineering}
      />
    </>
  );
}
